import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  combinePublishAtParts,
  parsePublishAtParts,
} from "../../utils/noticeScheduling";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
} from "../../utils/eventDateDisplay";
import { composerEventDetailFieldSx } from "../CreationWizard/composerBorderStyles";

export type PublishMode = "now" | "schedule";

export type PublishScheduleOptionsProps = {
  publishMode: PublishMode;
  publishAt: string;
  disabled?: boolean;
  hideHeading?: boolean;
  /** Latest allowed publish moment (e.g. first event occurrence start). */
  maxDateTime?: Date | null;
  onPublishModeChange: (mode: PublishMode) => void;
  onPublishAtChange: (value: string) => void;
};

const PublishScheduleOptions: React.FC<PublishScheduleOptionsProps> = ({
  publishMode,
  publishAt,
  disabled = false,
  hideHeading = false,
  maxDateTime = null,
  onPublishModeChange,
  onPublishAtChange,
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const { date: publishDate, time: publishTime } = useMemo(
    () => parsePublishAtParts(publishAt),
    [publishAt]
  );

  const maxPublishDate = useMemo(() => {
    if (!maxDateTime || Number.isNaN(maxDateTime.getTime())) return undefined;
    const d = new Date(maxDateTime);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [maxDateTime]);

  const updatePublishAt = (date: Date | null, time: string) => {
    onPublishAtChange(combinePublishAtParts(date, time));
  };

  const eventDetailFieldSx = composerEventDetailFieldSx;

  const pickerSlotProps = {
    textField: {
      fullWidth: true,
      size: "small" as const,
      sx: eventDetailFieldSx,
    },
    openPickerIcon: {
      sx: { fontSize: "1.25rem" },
    },
    popper: {
      sx: {
        // Above CreationWizard Dialog + sticky footer so calendar clicks work.
        zIndex: (theme: { zIndex: { modal: number } }) =>
          theme.zIndex.modal + 4,
      },
    },
  };

  return (
    <Box>
      {!hideHeading && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: "primary.main",
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            mb: 1,
          }}
        >
          When should this go live?
        </Typography>
      )}
      <FormControl
        component="fieldset"
        disabled={disabled}
        sx={{ width: "100%" }}
      >
        <RadioGroup
          row
          value={publishMode}
          onChange={(e) =>
            onPublishModeChange(e.target.value as PublishMode)
          }
          sx={{ gap: { xs: 0, sm: 2 } }}
        >
          <FormControlLabel
            value="now"
            control={<Radio size="small" />}
            label="Post now"
          />
          <FormControlLabel
            value="schedule"
            control={<Radio size="small" />}
            label="Schedule for later"
          />
        </RadioGroup>
      </FormControl>
      {publishMode === "schedule" && (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={EVENT_DATE_PICKER_LOCALE}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              mt: 1,
            }}
          >
            <DatePicker
              label="Publish date"
              format={EVENT_DATE_PICKER_FORMAT}
              value={publishDate}
              minDate={today}
              maxDate={maxPublishDate}
              disabled={disabled}
              enableAccessibleFieldDOMStructure={false}
              onChange={(date) => updatePublishAt(date, publishTime || "09:00")}
              slotProps={pickerSlotProps}
            />
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Publish time"
              value={publishTime || ""}
              disabled={disabled}
              onChange={(e) =>
                updatePublishAt(publishDate, e.target.value)
              }
              InputLabelProps={{ shrink: true }}
              sx={eventDetailFieldSx}
            />
          </Box>
        </LocalizationProvider>
      )}
    </Box>
  );
};

export default PublishScheduleOptions;
